import { InterestModellingFormData } from '../components/InterestModellingForm'

export interface CalculationResult {
  year: number
  age: number
  startingBalance: number
  drawdown: number
  interest: number
  repayments: number
  loanBalance: number
  propertyValue: number
  endingEstate: number
  noPropertyGrowthEndingValue: number
  noPropertyGrowthEndingEstate: number
}

export interface ChartDataPoint {
  label: string
  loanBalance: number
  propertyValue: number
  endingEstate: number
}

export const calculateInterestProjection = (
  formData: InterestModellingFormData
): { results: CalculationResult[]; chartData: ChartDataPoint[] } => {
  const {
    youngerAge,
    projectionBasis,
    customTerm,
    initialLoan,
    feesAdded,
    interestBasis,
    interestRate,
    propertyValue,
    propertyGrowth,
    includeRepayments,
    repaymentType,
    monthlyAmount,
    interestPercent,
    repaymentTerm,
    applyDrawdown,
    drawdownAmount,
    drawdownYear,
  } = formData

  // Calculate effective annual rate
  let effectiveAnnualRate = interestRate
  if (interestBasis === 'MER') {
    effectiveAnnualRate = (Math.pow(1 + interestRate / 100 / 12, 12) - 1) * 100
  }

  const monthlyInterestFactor = Math.pow(1 + effectiveAnnualRate / 100, 1 / 12)

  // Determine max projection years
  let maxProjectionYears: number
  if (projectionBasis === 'age') {
    maxProjectionYears = Math.max(85 - youngerAge, 15)
  } else {
    maxProjectionYears = customTerm
  }

  // Initialize calculation variables
  let currentLoanBalance = initialLoan + feesAdded
  let currentPropertyValue = propertyValue
  const propertyGrowthDecimal = propertyGrowth / 100

  const results: CalculationResult[] = []
  const chartData: ChartDataPoint[] = []

  // Calculate fixed monthly percent repayment if needed
  let fixedMonthlyPercentRepay = 0
  if (includeRepayments && repaymentType === 'percent') {
    const initialLoanMonthlyInterest = (initialLoan + feesAdded) * (monthlyInterestFactor - 1)
    fixedMonthlyPercentRepay = initialLoanMonthlyInterest * (interestPercent / 100)
  }

  // Calculate no property growth scenario (propertyGrowth = 0) in parallel
  let noGrowthLoanBalance = initialLoan + feesAdded
  let noGrowthPropertyValue = propertyValue

  for (let year = 1; year <= maxProjectionYears; year++) {
    // --- Main calculation (with user-supplied property growth) ---
    const currentAge = youngerAge + year - 1
    let yearlyInterestAccrued = 0
    let yearlyRepaymentsMade = 0
    let drawdownAppliedThisYear = 0
    const startingBalanceForYear = currentLoanBalance

    // --- No property growth calculation ---
    // let noGrowthDrawdownAppliedThisYear = 0
    // const noGrowthStartingBalanceForYear = noGrowthLoanBalance

    // Apply drawdown if applicable
    if (applyDrawdown && year === drawdownYear) {
      currentLoanBalance += drawdownAmount
      drawdownAppliedThisYear = drawdownAmount
      noGrowthLoanBalance += drawdownAmount
      // noGrowthDrawdownAppliedThisYear = drawdownAmount
    }

    // Monthly calculations (main)
    for (let month = 0; month < 12; month++) {
      const monthlyInterest = currentLoanBalance * (monthlyInterestFactor - 1)
      yearlyInterestAccrued += monthlyInterest
      currentLoanBalance += monthlyInterest

      // Calculate repayments
      let currentRepayment = 0
      if (includeRepayments && year <= repaymentTerm) {
        if (repaymentType === 'monthly') {
          currentRepayment = monthlyAmount
        } else if (repaymentType === 'percent') {
          currentRepayment = fixedMonthlyPercentRepay
        }
        currentRepayment = Math.min(currentRepayment, currentLoanBalance)
        currentLoanBalance -= currentRepayment
        yearlyRepaymentsMade += currentRepayment
      }
      currentLoanBalance = Math.max(currentLoanBalance, 0)
    }

    // Monthly calculations (no property growth)
    for (let month = 0; month < 12; month++) {
      const monthlyInterest = noGrowthLoanBalance * (monthlyInterestFactor - 1)
      noGrowthLoanBalance += monthlyInterest
      // Calculate repayments
      let currentRepayment = 0
      if (includeRepayments && year <= repaymentTerm) {
        if (repaymentType === 'monthly') {
          currentRepayment = monthlyAmount
        } else if (repaymentType === 'percent') {
          currentRepayment = fixedMonthlyPercentRepay
        }
        currentRepayment = Math.min(currentRepayment, noGrowthLoanBalance)
        noGrowthLoanBalance -= currentRepayment
      }
      noGrowthLoanBalance = Math.max(noGrowthLoanBalance, 0)
    }

    // Update property value
    currentPropertyValue *= (1 + propertyGrowthDecimal)
    const endingEstate = Math.max(currentPropertyValue - currentLoanBalance, 0)

    // No property growth: property value stays the same
    const noGrowthEndingEstate = Math.max(noGrowthPropertyValue - noGrowthLoanBalance, 0)

    // Store results with both growth and no-growth values
    const result: CalculationResult = {
      year,
      age: currentAge,
      startingBalance: Number(startingBalanceForYear.toFixed(2)),
      drawdown: Number(drawdownAppliedThisYear.toFixed(2)),
      interest: Number(yearlyInterestAccrued.toFixed(2)),
      repayments: Number(yearlyRepaymentsMade.toFixed(2)),
      loanBalance: Number(currentLoanBalance.toFixed(2)),
      propertyValue: Number(currentPropertyValue.toFixed(2)),
      endingEstate: Number(endingEstate.toFixed(2)),
      noPropertyGrowthEndingValue: Number(noGrowthPropertyValue.toFixed(2)),
      noPropertyGrowthEndingEstate: Number(noGrowthEndingEstate.toFixed(2)),
    }

    results.push(result)

    // Chart data
    chartData.push({
      label: `Y${year} (Age ${currentAge})`,
      loanBalance: result.loanBalance,
      propertyValue: result.propertyValue,
      endingEstate: result.endingEstate,
    })
  }

  return { results, chartData }
}

// Helper function to convert calculation results to the format expected by the existing table
export const convertToAnnualInterestDataSource = (results: CalculationResult[]) => {
  return results.map((result, index) => ({
    id: index,
    year: result.year,
    startingBalance: result.startingBalance,
    additionalPayment: result.drawdown || null,
    annualInterest: result.interest,
    endingBalance: result.loanBalance,
    noPropertyGrowthEndingValue: result.noPropertyGrowthEndingValue,
    noPropertyGrowthEndingEstate: result.noPropertyGrowthEndingEstate,
    propertyGrowthEndingValue: result.propertyValue,
    propertyGrowthEndingEstate: result.endingEstate,
  }))
}